/**
 * StockOverviewPanel: the "balanced dashboard" a stock opens with — a price
 * graph, the latest news, key stats, and the standalone risk read side by
 * side, so a first glance answers "how is this doing and what's happening?".
 * Narrative copy (news summaries, risk notes) uses the redacted-reveal
 * animation.
 */

import React, { useEffect, useState } from "react";
import { apiClient } from "@/api/client";
import type { CompanyRiskAssessment, FundamentalSnapshot, NewsItem, PriceBar } from "@/types";
import LineChart from "@/components/charts/LineChart";
import RevealText from "@/components/ui/RevealText";

interface StockOverviewPanelProps {
  companyId: string;
  symbol: string;
  priceBars: PriceBar[];
  fundamentals: FundamentalSnapshot | null;
  risk: CompanyRiskAssessment | null;
}

const SENTIMENT_TONE: Record<NewsItem["sentiment"], string> = {
  positive: "text-signal-teal border-signal-teal/40 bg-signal-teal/10",
  neutral: "text-mist-300 border-ink-600 bg-ink-800",
  negative: "text-signal-rose border-signal-rose/40 bg-signal-rose/10",
};

const RATING_COPY: Record<CompanyRiskAssessment["risk_rating"], { label: string; tone: string }> = {
  low: { label: "Low risk", tone: "text-signal-teal" },
  moderate: { label: "Moderate risk", tone: "text-mist-200" },
  elevated: { label: "Elevated risk", tone: "text-signal-amber" },
  high: { label: "High risk", tone: "text-signal-rose" },
};

function fmt(value: number | string | null | undefined, suffix = "", decimals = 2): string {
  if (value === null || value === undefined) return "—";
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(decimals)}${suffix}` : "—";
}

function Stat({ label, value, tone = "text-mist-100" }: { label: string; value: string; tone?: string }): React.ReactElement {
  return (
    <div className="rounded-lg border border-ink-700 bg-ink-800/40 p-3">
      <p className="text-xs text-mist-400">{label}</p>
      <p className={`mt-1 font-mono text-sm ${tone}`}>{value}</p>
    </div>
  );
}

export default function StockOverviewPanel({
  companyId,
  symbol,
  priceBars,
  fundamentals,
  risk,
}: StockOverviewPanelProps): React.ReactElement {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setNewsLoading(true);
    apiClient
      .get<NewsItem[]>(`/companies/${companyId}/news`)
      .then(({ data }) => {
        if (!cancelled) setNews((data ?? []).slice(0, 5));
      })
      .catch(() => {
        if (!cancelled) setNews([]);
      })
      .finally(() => {
        if (!cancelled) setNewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const closes = priceBars
    .map((bar) => Number(bar.close))
    .filter((value) => Number.isFinite(value));
  const last = closes.length > 0 ? closes[closes.length - 1] : null;
  const first = closes.length > 0 ? closes[0] : null;
  const changePct = last !== null && first ? ((last - first) / first) * 100 : null;
  const rating = risk ? RATING_COPY[risk.risk_rating] : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Price graph */}
        <div className="panel p-5 lg:col-span-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold text-mist-100">Price overview</h2>
            {last !== null && (
              <p className="font-mono text-sm text-mist-200">
                ${last.toFixed(2)}
                {changePct !== null && (
                  <span className={`ml-2 ${changePct >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                    {changePct >= 0 ? "+" : ""}
                    {changePct.toFixed(2)}%
                  </span>
                )}
              </p>
            )}
          </div>
          <div className="mt-4 overflow-x-auto">
            {closes.length > 0 ? (
              <LineChart data={closes} width={560} height={150} />
            ) : (
              <p className="py-10 text-center text-sm text-mist-400">
                No price history yet — fetch data to draw the graph.
              </p>
            )}
          </div>
        </div>

        {/* Risk & volatility */}
        <div className="panel p-5">
          <h2 className="text-lg font-semibold text-mist-100">Risk &amp; volatility</h2>
          {risk ? (
            <>
              <p className={`mt-2 text-xl font-semibold ${rating?.tone ?? "text-mist-200"}`}>
                {rating?.label ?? "—"}
              </p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <Stat label="Volatility (ann.)" value={fmt(risk.volatility_annualized_pct, "%", 1)} />
                <Stat label="Max drawdown" value={fmt(risk.max_drawdown_pct, "%", 1)} />
                <Stat label="Sharpe" value={fmt(risk.sharpe_ratio)} />
                <Stat label="Beta" value={fmt(risk.beta)} />
              </div>
              {risk.flags.length > 0 && (
                <p className="mt-3 text-xs text-mist-400">
                  <RevealText delay={120}>{risk.flags.join(" · ")}</RevealText>
                </p>
              )}
            </>
          ) : (
            <p className="mt-2 text-sm text-mist-400">No risk assessment yet.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Latest news */}
        <div className="panel p-5 lg:col-span-2">
          <h2 className="text-lg font-semibold text-mist-100">Latest news</h2>
          {newsLoading ? (
            <p className="mt-3 text-sm text-mist-400">Loading news…</p>
          ) : news.length === 0 ? (
            <p className="mt-3 text-sm text-mist-400">
              No news recorded yet for {symbol}. Use "Fetch latest data" to pull the latest items.
            </p>
          ) : (
            <ul className="mt-3 space-y-3">
              {news.map((item, index) => (
                <li key={item.id} className="border-b border-ink-800 pb-3 last:border-0 last:pb-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${SENTIMENT_TONE[item.sentiment]}`}>
                      {item.sentiment}
                    </span>
                    <span className="text-xs text-mist-500">
                      {item.source} · {new Date(item.published_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-medium text-mist-100">
                    {item.url ? (
                      <a href={item.url} target="_blank" rel="noreferrer" className="hover:text-signal-teal hover:underline">
                        {item.headline}
                      </a>
                    ) : (
                      item.headline
                    )}
                  </p>
                  {item.summary && (
                    <p className="mt-1 text-sm text-mist-300">
                      <RevealText delay={index * 90}>{item.summary}</RevealText>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Key stats */}
        <div className="panel p-5">
          <h2 className="text-lg font-semibold text-mist-100">Key stats</h2>
          {fundamentals ? (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Stat label="P/E" value={fmt(fundamentals.pe_ratio)} />
              <Stat label="PEG" value={fmt(fundamentals.peg_ratio)} />
              <Stat label="Rev. growth" value={fmt(fundamentals.revenue_growth_yoy, "%")} />
              <Stat label="Net margin" value={fmt(fundamentals.net_margin, "%")} />
              <Stat label="ROE" value={fmt(fundamentals.roe, "%")} />
              <Stat label="Div. yield" value={fmt(fundamentals.dividend_yield, "%")} />
              <Stat label="52w high" value={fundamentals.week52_high ? `$${fmt(fundamentals.week52_high)}` : "—"} />
              <Stat label="52w low" value={fundamentals.week52_low ? `$${fmt(fundamentals.week52_low)}` : "—"} />
            </div>
          ) : (
            <p className="mt-3 text-sm text-mist-400">No fundamentals on file yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}
